import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../hooks/useAuth';
import Drawer from '../shared/Drawer';
import { format } from 'date-fns';

const emptyForm = {
  client_id: '', project_name: '', category_id: '', designer_id: '', status_id: '',
  mode: 'Billable', invoice_no: '', remarks: '', due_date: '', job_no_override: ''
};

export default function JobDrawer({ open, onClose, job, statuses, designers, clients, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!job;

  const [form, setForm] = useState(emptyForm);
  const [clientSearch, setClientSearch] = useState('');
  const [showPred, setShowPred] = useState(false);
  const [logs, setLogs] = useState([]);
  const predRef = useRef(null);

  const { data: cats = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && job) {
      setForm({
        client_id: job.client_id || '',
        project_name: job.project_name || '',
        category_id: job.category_id || '',
        designer_id: job.designer_id || '',
        status_id: job.status_id || '',
        mode: job.mode || 'Billable',
        invoice_no: job.invoice_no || '',
        remarks: job.remarks || '',
        due_date: job.due_date ? job.due_date.slice(0, 10) : '',
        job_no_override: '',
      });
      setClientSearch(job.client_name || '');
      api.get(`/jobs/${job.id}`).then(r => setLogs(r.data.logs || [])).catch(() => {});
    } else if (!isEdit) {
      setForm(emptyForm);
      setClientSearch('');
      setLogs([]);
    }
  }, [open, job, isEdit]);

  useEffect(() => {
    if (!open || isEdit || !statuses?.length) return;
    setForm(f => {
      if (f.status_id) return f;
      const newId = statuses.find(s => s.name === 'New')?.id;
      if (newId == null) return f;
      return { ...f, status_id: String(newId) };
    });
  }, [open, isEdit, statuses]);

  useEffect(() => {
    const handler = e => { if (predRef.current && !predRef.current.contains(e.target)) setShowPred(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const filteredClients = clientSearch
    ? clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase()))
    : clients;

  const saveMut = useMutation({
    mutationFn: payload => isEdit
      ? api.put(`/jobs/${job.id}`, payload)
      : api.post('/jobs', payload),
    onSuccess: () => {
      toast.success(isEdit ? 'Job updated' : 'Job created');
      onSaved();
      onClose();
    },
  });

  const handleSubmit = () => {
    if (!form.client_id || !form.project_name || !form.category_id || !form.status_id)
      return toast.error('Client, Project Name, Category and Status are required');
    const payload = { ...form };
    if (!payload.job_no_override) delete payload.job_no_override;
    saveMut.mutate(payload);
  };

  const inp = { width: '100%', padding: '7px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12, color: '#343a40', background: '#fff' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 };
  const fg = { marginBottom: 11 };

  return (
    <Drawer
      open={open} onClose={onClose}
      title={isEdit ? 'Edit Job' : 'Create Job'}
      footer={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : isEdit ? 'Update Job' : 'Save Job'}
          </button>
        </>
      }
    >
      {isEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13, padding: '9px 12px', background: '#e8f3fb', borderRadius: 7, borderLeft: '3px solid #0f74b9' }}>
          <div>
            <div style={{ fontSize: 10, color: '#6c757d' }}>Job Number</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f74b9', fontFamily: 'monospace' }}>{job?.job_no}</div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#6c757d' }}>Created</div>
            <div style={{ fontSize: 12 }}>{job?.created_at ? format(new Date(job.created_at), 'dd MMM yyyy') : '—'}</div>
          </div>
        </div>
      )}

      {isEdit && user.role === 'super_admin' && (
        <div style={fg}>
          <label style={lbl}>Override Job No (Super Admin only)</label>
          <input style={inp} value={form.job_no_override} onChange={e => set('job_no_override', e.target.value)} placeholder="Leave blank to keep current" />
        </div>
      )}

      <div className="form-row">
        <div style={fg}>
          <label style={lbl}>Client *</label>
          <div ref={predRef} style={{ position: 'relative' }}>
            <input style={inp} value={clientSearch}
              onChange={e => { setClientSearch(e.target.value); set('client_id', ''); setShowPred(true); }}
              onFocus={() => setShowPred(true)}
              placeholder="Type to search client…" />
            {showPred && filteredClients.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ced4da', borderRadius: 6, maxHeight: 170, overflowY: 'auto', zIndex: 300, boxShadow: '0 4px 12px rgba(0,0,0,.1)' }}>
                {filteredClients.map(c => (
                  <div key={c.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') { set('client_id', c.id); setClientSearch(c.name); setShowPred(false); } }}
                    onClick={() => { set('client_id', c.id); setClientSearch(c.name); setShowPred(false); }}
                    style={{ padding: '7px 10px', fontSize: 12, cursor: 'pointer', color: '#343a40' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#e8f3fb'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                    {c.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={fg}>
          <label style={lbl}>Designer</label>
          <select style={inp} value={form.designer_id} onChange={e => set('designer_id', e.target.value)}>
            <option value="">Unassigned</option>
            {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div style={fg}>
          <label style={lbl}>Project Name *</label>
          <input style={inp} value={form.project_name} onChange={e => set('project_name', e.target.value)} placeholder="Project name" />
        </div>
        <div style={fg}>
          <label style={lbl}>Status *</label>
          <select style={inp} value={form.status_id} onChange={e => set('status_id', e.target.value)}>
            <option value="">Select status</option>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div style={fg}>
          <label style={lbl}>Category *</label>
          <select style={inp} value={form.category_id} onChange={e => set('category_id', e.target.value)}>
            <option value="">Select category</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div style={fg}>
          <label style={lbl}>Mode</label>
          <select style={inp} value={form.mode} onChange={e => set('mode', e.target.value)}>
            {['Internal', 'Retainer', 'Billable', 'Non - Billable'].map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div style={fg}>
          <label style={lbl}>Due Date</label>
          <input type="date" style={inp} value={form.due_date} onChange={e => set('due_date', e.target.value)} />
        </div>
        <div style={fg}>
          <label style={lbl}>Invoice No</label>
          <input style={inp} value={form.invoice_no} onChange={e => set('invoice_no', e.target.value)} placeholder="INV-XXXX" />
        </div>
      </div>

      <div style={fg}>
        <label style={lbl}>Remarks</label>
        <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={form.remarks} onChange={e => set('remarks', e.target.value)} placeholder="Add notes or remarks…" />
      </div>

      {isEdit && (
        <div style={{ background: '#f8f9fa', borderRadius: 7, padding: 12, marginTop: 4, fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: '#495057', marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: .4 }}>Job History</div>
          <div style={{ color: '#6c757d', marginBottom: 4 }}>Created by: <strong style={{ color: '#343a40' }}>{job?.created_by_name || '—'}</strong></div>
          <div style={{ color: '#6c757d', marginBottom: 4 }}>Last updated: <strong style={{ color: '#343a40' }}>{job?.updated_at ? format(new Date(job.updated_at), 'dd MMM yyyy') : '—'}</strong></div>
          <div style={{ color: '#6c757d' }}>Assigned to: <strong style={{ color: '#343a40' }}>{job?.designer_name || 'Unassigned'}</strong></div>

          {logs.length > 0 && (
            <>
              <div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid #e9ecef', fontWeight: 600, color: '#495057', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 8 }}>Activity Timeline</div>
              {logs.map(l => (
                <div key={l.id} style={{ display: 'flex', gap: 8, marginBottom: 9 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#0f74b9', marginTop: 4, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: '#343a40', fontSize: 11 }}>
                      {l.action === 'status_changed' ? `Status changed to ${l.new_value}` :
                        l.action === 'assigned' ? `Assigned to ${l.new_value}` :
                          l.action === 'created' ? 'Job created' :
                            l.action === 'archived' ? 'Job archived' :
                              l.action === 'updated' && l.field_name ? `${l.field_name} updated` :
                                `${l.field_name || 'Field'} updated`}
                    </div>
                    <div style={{ color: '#6c757d', fontSize: 11 }}>
                      {l.user_name} · {l.created_at ? format(new Date(l.created_at), 'dd MMM yyyy, HH:mm') : ''}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}
